import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ActivityIndicator,
  Linking,
  Platform,
  Image,
} from 'react-native';
import { AuthManager } from '../utils/authManager';
import { UnifiedAuthService } from '../services/unifiedAuthService';
import { appointmentManager, Appointment } from '../utils/appointmentManager';
import { UserTrialManager, UserTrialData } from '../utils/userTrialManager';
import UserBottomMenu from '../components/UserBottomMenu';
import SignOutConfirmModal from '../components/SignOutConfirmModal';
import UserAppointmentsScreen from './UserAppointmentsScreen';
import UserProfileScreen from './UserProfileScreen';
import UserFavoritesScreen from './UserFavoritesScreen';
import BusinessExplorationScreen from './BusinessExplorationScreen';
import OneClickBookingScreen from './OneClickBookingScreen';
import BusinessBookingModal from '../components/BusinessBookingModal';
import { instantBookingService, InstantBooking } from '../services/instantBookingService';
import { favoritesService, FavoriteBusiness } from '../services/favoritesService';
import { appointmentRequestService, AppointmentRequest } from '../services/appointmentRequestService';
import CreditsPurchaseScreen from './CreditsPurchaseScreen';
import creditsService, { UserCredits } from '../services/creditsService';
import membershipService from '../services/membershipService';
import type { StaffMembership } from '../types/staff';
import WorkAppointmentsScreen from './WorkAppointmentsScreen.tsx';
import type { Business } from '../types/business';
import {
  buildWidgetEmbedBookingCompletePayload,
  consumeWebAppInitialTab,
  isWidgetEmbedMode,
  notifyWidgetEmbedParent,
} from '../utils/widgetEmbed';

interface UserDashboardScreenProps {
  onLogout: () => void;
  onNavigateToChat?: () => void;
  onNavigateToSubscription?: () => void;
  onNavigateToSubscriptionManagement?: () => void;
  onNavigateToCreditsPurchase?: () => void;
  username?: string;
  triggerOneClickBooking?: boolean;
  onOneClickBookingTriggered?: () => void;
  /** When set by App (e.g. Book from standalone Favorites route), open one-click booking for this business */
  pendingBookFromFavorites?: Business | null;
  onPendingBookFromFavoritesConsumed?: () => void;
  /** From embedded widget: `?widgetBook=1&businessId=` and optional `serviceId` — App passes after reading the URL. */
  widgetBookingParams?: { businessId: string; serviceId?: string } | null;
  onWidgetBookingConsumed?: () => void;
  /** Open a specific tab on first mount (e.g. appointments after guest booking). */
  initialActiveTab?: 'dashboard' | 'appointments';
  onInitialActiveTabConsumed?: () => void;
}

interface NearbyBusiness {
  id: string;
  name: string;
  category: string;
  rating: number;
  distance: string;
  address: string;
  phone: string;
  services: string[];
  isOpen: boolean;
  nextAvailableSlot?: string;
  imageUrl?: string;
  priceRange: string;
  hours: string;
  isFavorite: boolean;
}

const UserDashboardScreen: React.FC<UserDashboardScreenProps> = ({
  onLogout,
  onNavigateToChat,
  onNavigateToSubscription,
  onNavigateToSubscriptionManagement,
  onNavigateToCreditsPurchase,
  username,
  triggerOneClickBooking = false,
  onOneClickBookingTriggered,
  pendingBookFromFavorites,
  onPendingBookFromFavoritesConsumed,
  widgetBookingParams,
  onWidgetBookingConsumed,
  initialActiveTab,
  onInitialActiveTabConsumed,
}) => {
  const [showCreditsPurchase, setShowCreditsPurchase] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [nearbyBusinesses, setNearbyBusinesses] = useState<NearbyBusiness[]>([]);
  const [favoriteBusinesses, setFavoriteBusinesses] = useState<FavoriteBusiness[]>([]);
  const [currentTrial, setCurrentTrial] = useState<UserTrialData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'appointments' | 'work' | 'favorites' | 'business-exploration' | 'profile'>('dashboard');
  const [appointmentsInitialFilter, setAppointmentsInitialFilter] = useState<'upcoming' | 'today'>('upcoming');
  const [showOneClickBooking, setShowOneClickBooking] = useState(false);
  /** Service id from widget deep link; passed into OneClick to skip the service list when possible. */
  const [widgetPreSelectedServiceId, setWidgetPreSelectedServiceId] = useState<string | null>(null);
  const [userCredits, setUserCredits] = useState<UserCredits | null>(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [memberships, setMemberships] = useState<StaffMembership[]>([]);
  const [activeMembership, setActiveMembership] = useState<StaffMembership | null>(null);

  // Debug: Track showOneClickBooking state changes (commented out for production)
  // useEffect(() => {
  //   console.log('🔄 showOneClickBooking state changed to:', showOneClickBooking);
  // }, [showOneClickBooking]);

  // Stable callback for booking from favorites
  const handleBookFromFavorites = useCallback((business: any) => {
    console.log('📅 handleBookFromFavorites called with business:', business?.name);
    console.log('📅 Setting selectedBusiness and showing booking screen...');
    // Direct booking from favorites
    setSelectedBusiness(business);
    setShowOneClickBooking(true);
    console.log('📅 Booking screen should now be visible');
  }, []);

  const [instantBookings, setInstantBookings] = useState<InstantBooking[]>([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<NearbyBusiness | null>(null);

  useEffect(() => {
    loadUserData();
    // Don't load nearby businesses on mount — gives backend time to warm up (e.g. 65s). Geocoding + Foursquare run when user taps Explore.
    loadTrialData();
    loadInstantBookings();
    loadFavorites();
  }, []);

  useEffect(() => {
    const tabFromWidgetRedirect = consumeWebAppInitialTab();
    if (tabFromWidgetRedirect) {
      setActiveTab(tabFromWidgetRedirect);
    }
  }, []);

  useEffect(() => {
    if (initialActiveTab) {
      setActiveTab(initialActiveTab);
      onInitialActiveTabConsumed?.();
    }
  }, [initialActiveTab, onInitialActiveTabConsumed]);

  // Load credits when user is available (force refresh to get latest)
  useEffect(() => {
    if (user) {
      loadUserCredits(true); // Force refresh to bypass cache
    }
  }, [user]);
  
  // Also reload credits when returning to dashboard tab
  useEffect(() => {
    if (activeTab === 'dashboard' && user) {
      loadUserCredits(true); // Force refresh
    }
  }, [activeTab, user]);

  // Reload user data and favorites when returning to dashboard tab (in case profile was updated)
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadUserData();
      loadFavorites();
    }
  }, [activeTab]);

  // Load appointments after user data is loaded (so we can filter by user)
  useEffect(() => {
    if (user) {
      loadAppointments();
    }
  }, [user]);

  // Reload when opening Bookings so the list matches storage/Firestore (avoids stale tab props).
  useEffect(() => {
    if (activeTab === 'appointments' && user) {
      loadAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user]);

  // Load staff memberships (enables Work tab)
  useEffect(() => {
    const loadMemberships = async () => {
      try {
        if (!user) return;
        const userId = (user as any).uid || user.id;
        if (!userId) return;
        const ms = await membershipService.getMembershipsForUser(userId);
        setMemberships(ms);
        setActiveMembership(ms[0] ?? null);
      } catch (e) {
        console.warn('Failed to load memberships:', e);
        setMemberships([]);
        setActiveMembership(null);
      }
    };
    loadMemberships();
  }, [user]);

  // Trigger 30 sec booking if coming from chat
  useEffect(() => {
    if (triggerOneClickBooking) {
      setShowOneClickBooking(true);
      // Notify parent that trigger has been used
      onOneClickBookingTriggered?.();
    }
  }, [triggerOneClickBooking, onOneClickBookingTriggered]);

  // Standalone Favorites screen (App route) passes business here then navigates to user-dashboard
  useEffect(() => {
    if (!pendingBookFromFavorites) return;
    setSelectedBusiness(pendingBookFromFavorites as unknown as NearbyBusiness);
    setShowOneClickBooking(true);
    onPendingBookFromFavoritesConsumed?.();
    // Only when pending business changes; parent often passes an inline consumer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingBookFromFavorites]);

  // Embedded site widget: same API the widget uses to list services (public CORS).
  const WIDGET_BOOKING_API = 'https://bookapt-payment-api.onrender.com/api';
  useEffect(() => {
    if (!widgetBookingParams || !user) return;
    const { businessId, serviceId } = widgetBookingParams;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${WIDGET_BOOKING_API}/business/${encodeURIComponent(businessId)}`
        );
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data?.success) {
          Alert.alert(
            'Booking',
            'We could not open this business. Try again or book from the BookAPT app.'
          );
          onWidgetBookingConsumed?.();
          return;
        }
        const businessCategory =
          data.categoryId || data.industry || data.category || 'services';
        setSelectedBusiness({
          id: data.id,
          name: data.businessName || 'Business',
          category: businessCategory,
          type: businessCategory,
          rating: 0,
          distance: '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          services: [],
          isOpen: true,
          priceRange: '',
          hours: '',
          isFavorite: false,
        } as unknown as NearbyBusiness);
        setWidgetPreSelectedServiceId(serviceId ?? null);
        setShowOneClickBooking(true);
        onWidgetBookingConsumed?.();
      } catch {
        if (!cancelled) {
          Alert.alert('Booking', 'Could not load this business. Check your connection and try again.');
          onWidgetBookingConsumed?.();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [widgetBookingParams, user, onWidgetBookingConsumed]);

  const loadUserData = async () => {
    try {
      // Use UnifiedAuthService for both web and mobile to ensure we read from Firestore
      // UnifiedAuthService correctly handles both platforms and reads from Firestore
      const userData: any = await UnifiedAuthService.getCurrentUser();
      console.log('🔍 UserDashboard: Loaded user from UnifiedAuthService:', userData?.email, 'name:', userData?.name, `(${Platform.OS})`);
      
      if (!userData) {
        console.warn('⚠️ UserDashboard: No user data found');
      } else {
        console.log('✅ UserDashboard: User loaded:', {
          email: userData.email,
          name: userData.name,
          address: userData.address,
        });
        console.log('✅ UserDashboard: Address type:', typeof userData.address);
        console.log('✅ UserDashboard: Address value:', JSON.stringify(userData.address, null, 2));
        console.log('✅ UserDashboard: Formatted address:', formatAddress(userData.address));
      }
      
      setUser(userData);
    } catch (error) {
      console.error('❌ Error loading user data:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUserCredits = async (forceRefresh: boolean = false) => {
    if (!user) return;
    
    try {
      setLoadingCredits(true);
      const userId = (user as any).uid || user.id;
      if (userId) {
        // Clear cache if force refresh
        if (forceRefresh) {
          await creditsService.clearCreditsCache();
        }
        
        const credits = await creditsService.getUserCredits(userId, forceRefresh);
        setUserCredits(credits);
        console.log('✅ UserDashboardScreen: Credits loaded - Available:', credits.availableCredits, 'Purchased:', credits.purchasedCredits);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setLoadingCredits(false);
    }
  };

  const loadAppointments = async () => {
    try {
      // Load regular appointments
      const allAppointments = await appointmentManager.getAppointments();
      let userAppointments: Appointment[] = [];
      
      // Filter appointments for this user by email or name
      if (user) {
        console.log('🔍 UserDashboard: Filtering appointments for user:', {
          userEmail: user.email,
          userUsername: user.username,
          userName: user.name,
          totalAppointments: allAppointments.length
        });
        
        userAppointments = (allAppointments || []).filter(apt => {
          if (!apt) return false;
          const userId = ((user as any).id || (user as any).uid || '').trim();
          const userEmail = (user.email || '').toLowerCase().trim();
          const userUsername = (user.username || '').toLowerCase().trim();
          const userPhone = (user.phone || '').trim();
          const userName = (user.name || '').trim();
          const aptCustomerId = (((apt as any).customerId || '') as string).trim();
          const aptEmail = (apt.email || '').toLowerCase().trim();
          const aptPhone = (apt.phone || '').trim();
          const aptName = (apt.customerName || '').trim();

          const idMatch = !!userId && !!aptCustomerId && aptCustomerId === userId;
          const emailMatch = !!aptEmail && (aptEmail === userEmail || aptEmail === userUsername);
          const phoneMatch = !!aptPhone && !!userPhone && aptPhone === userPhone;
          const nameMatch = !!aptName && !!userName && aptName === userName;
          const matches = idMatch || emailMatch || phoneMatch || nameMatch;
          
          if (matches) {
            console.log('✅ UserDashboard: Found matching appointment:', {
              id: apt.id,
              email: apt.email,
              customerName: apt.customerName,
              serviceType: apt.serviceType,
              appointmentDate: apt.appointmentDate
            });
          }
          
          return matches;
        });
        
        console.log('🔍 UserDashboard: Filtered appointments:', userAppointments.length);
        
        // Load pending appointment requests and convert them to appointments
        // Use Promise.race with timeout to prevent blocking
        try {
          const userId = (user as any).id || (user as any).uid || '';
          const userEmail = user.email || '';
          const userPhone = user.phone || '';
          
          // Add timeout to prevent blocking (3 seconds max)
          const timeoutPromise = new Promise<AppointmentRequest[]>((resolve) => 
            setTimeout(() => resolve([]), 3000)
          );
          
          const requestsPromise = appointmentRequestService.getAllRequests();
          const allRequests = await Promise.race([requestsPromise, timeoutPromise]);
          
          const userRequests = allRequests.filter(req => {
            return req.userId === userId || 
                   req.userEmail?.toLowerCase() === userEmail.toLowerCase() ||
                   req.userPhone === userPhone;
          });
          
          // Convert appointment requests to appointment format for display
          const requestAppointments: Appointment[] = userRequests
            .filter(req => req.status === 'pending' || req.status === 'confirmed' || req.status === 'reschedule_requested')
            .map(req => ({
              // Prefix to avoid collisions with locally-stored appointment ids and keep React keys stable
              id: `req_${req.id}`,
              customerName: user?.name || 'You',
              customerId: req.userId,
              businessId: `external_${req.businessPhone}`,
              businessName: req.businessName,
              serviceType: req.service,
              appointmentDate: req.preferredDate,
              appointmentTime: req.preferredTime,
              status: req.status === 'pending' ? 'pending_business_confirmation' : 
                      req.status === 'confirmed' ? 'confirmed' : 
                      req.status === 'reschedule_requested' ? 'pending_reschedule' : 'pending',
              address: (req as any).businessLocation || '',
              phone: req.businessPhone,
              email: req.businessEmail || '',
              createdAt: req.createdAt,
              updatedAt: (req as any).updatedAt,
              specialInstructions: req.specialRequests || '',
            }));
          
          // Combine regular appointments with request appointments
          userAppointments = [...userAppointments, ...requestAppointments];
          
          // Sort by date (most recent first)
          userAppointments.sort((a, b) => {
            const dateA = new Date(a.appointmentDate).getTime();
            const dateB = new Date(b.appointmentDate).getTime();
            return dateB - dateA;
          });
        } catch (error) {
          console.error('Error loading appointment requests:', error);
          // Continue with regular appointments if request loading fails
        }
        
        console.log('🔍 UserDashboard: Loaded appointments:', userAppointments.length);
        setAppointments(userAppointments);
      } else {
        // If user not loaded yet, set empty array (will reload when user loads)
        console.log('🔍 UserDashboard: User not loaded yet, setting empty appointments');
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      setAppointments([]);
    }
  };

  const loadNearbyBusinesses = async () => {
    try {
      // Get user zipcode from Firestore
      let userZipcode: string | null = null;
      try {
        const { UnifiedAuthService } = await import('../services/unifiedAuthService');
        const currentUser = await UnifiedAuthService.getCurrentUser();
        if (currentUser?.address?.zipcode) {
          userZipcode = currentUser.address.zipcode;
          // console.log('📍 Loading businesses for user zipcode:', userZipcode);
        } else {
          // console.log('⚠️ User zipcode not found, cannot load nearby businesses');
          setNearbyBusinesses([]);
          return;
        }
      } catch (error) {
        console.error('Error getting user zipcode:', error);
        setNearbyBusinesses([]);
        return;
      }

      // Load real businesses from Firestore based on zipcode
      const { hybridBusinessSearchService } = await import('../services/hybridBusinessSearchService');
      
      // Search for businesses in user's area (using "all" to get nearby businesses)
      const searchResults = await hybridBusinessSearchService.search({
        query: 'all', // Get all businesses in area
        zipcode: userZipcode,
      });

      if (searchResults.totalFound > 0) {
        // Load favorites once (avoids 10× isFavorite calls)
        const favorites = await favoritesService.getFavoriteBusinesses();
        const favoriteIds = new Set(favorites.map((f) => f.id));

        // Convert to NearbyBusiness format with isFavorite from Set
        const businessesWithFavorites: NearbyBusiness[] = searchResults.businesses.slice(0, 10).map((biz: any) => ({
          id: biz.id,
          name: biz.name,
          category: biz.category || 'Business',
          rating: biz.rating || 4.0,
          distance: biz.distance ? `${(biz.distance / 1609).toFixed(1)} miles` : '0.5 miles',
          address: biz.address || `${biz.city || ''}, ${biz.state || ''} ${biz.zipcode || ''}`,
          phone: biz.phone || 'N/A',
          services: biz.services || [],
          isOpen: biz.isOpen !== false, // Default to open
          nextAvailableSlot: 'Check availability',
          imageUrl: biz.imageUrl,
          priceRange: biz.priceRange || '$$',
          hours: biz.hours || '9:00 AM - 6:00 PM',
          isFavorite: favoriteIds.has(biz.id),
        }));

        setNearbyBusinesses(businessesWithFavorites);
        // console.log(`✅ Loaded ${businessesWithFavorites.length} real businesses for zipcode ${userZipcode}`);
      } else {
        // console.log('ℹ️ No businesses found for zipcode:', userZipcode);
        setNearbyBusinesses([]);
      }
    } catch (error) {
      console.error('Error loading nearby businesses:', error);
      setNearbyBusinesses([]);
    }
  };

  const loadTrialData = async () => {
    try {
      const trialData = await UserTrialManager.getTrialData();
      setCurrentTrial(trialData);
    } catch (error) {
      console.error('Error loading trial data:', error);
    }
  };

  const loadInstantBookings = async () => {
    try {
      if (user?.id) {
        const bookings = await instantBookingService.getInstantBookings(user.id);
        setInstantBookings(bookings);
      }
    } catch (error) {
      console.error('Error loading instant bookings:', error);
    }
  };

  const loadFavorites = async () => {
    try {
      console.log('🔍 UserDashboard: Loading favorites...');
      const favorites = await favoritesService.getFavoriteBusinesses();
      console.log('✅ UserDashboard: Loaded favorites:', favorites.length);
      setFavoriteBusinesses(favorites);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavoriteBusinesses([]);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      loadUserData(),
      loadAppointments(),
      // loadNearbyBusinesses() omitted — Explore screen does geocoding + Foursquare when user opens it
      loadTrialData(),
      loadInstantBookings(),
      loadFavorites(),
    ]);
    setRefreshing(false);
  };

  const handleOneClickBooking = () => {
    // Clear selectedBusiness when opening 30-second booking from dashboard
    // This ensures the flow starts at Category → Business → Service
    setSelectedBusiness(null);
    setShowOneClickBooking(true);
  };

  const handleBookingComplete = async (booking: InstantBooking) => {
    console.log('🔵 UserDashboard: handleBookingComplete called with booking:', booking);
    setInstantBookings(prev => [booking, ...prev]);
    setWidgetPreSelectedServiceId(null);
    setShowOneClickBooking(false);

    if (isWidgetEmbedMode()) {
      notifyWidgetEmbedParent(
        'booking-complete',
        buildWidgetEmbedBookingCompletePayload(booking),
      );
      return;
    }
    
    // Small delay to ensure Firestore has processed the save
    await new Promise<void>((resolve) => setTimeout(() => resolve(), 500));
    
    // Reload appointments to show the new booking
    console.log('🔵 UserDashboard: Reloading appointments after booking...');
    await loadAppointments();
    
    // Reload credits to show updated balance after deduction
    if (user?.id) {
      try {
        await loadUserCredits();
      } catch (error) {
        console.error('Error reloading credits after booking:', error);
      }
    }
    
    // Navigate to appointments tab to show the new booking
    console.log('🔵 UserDashboard: Navigating to appointments tab');
    setActiveTab('appointments');
  };

  const handleBookAppointment = (business: NearbyBusiness) => {
    // Show booking modal with options
    setSelectedBusiness(business);
    setShowBookingModal(true);
  };

  const handleBookingModalBook = (options: any) => {
    // Close modal and proceed with booking
    setShowBookingModal(false);
    setShowOneClickBooking(true);
  };

  const handleAddToFavorites = async (businessId: string) => {
    try {
      const business = nearbyBusinesses.find(b => b.id === businessId);
      if (business) {
        const success = await favoritesService.addToFavorites({
          id: business.id,
          ownerId: (business as any).ownerId || (business as any).businessOwnerId || business.id,
          businessId: (business as any).businessId || business.id,
          firestoreId: (business as any).firestoreId,
          docId: (business as any).docId,
          name: business.name,
          category: business.category,
          rating: business.rating,
          distance: business.distance,
          address: business.address,
          phone: business.phone,
          imageUrl: business.imageUrl,
          services: business.services,
          priceRange: business.priceRange,
          hours: business.hours,
        });

        if (success) {
          // Update the business in the list
          setNearbyBusinesses(prev => 
            prev.map(b => b.id === businessId ? { ...b, isFavorite: true } : b)
          );
          // Reload favorites to update the favorites card
          await loadFavorites();
        }
      }
    } catch (error) {
      console.error('Error adding to favorites:', error);
    }
  };

  const handleRemoveFromFavorites = async (businessId: string) => {
    try {
      const success = await favoritesService.removeFromFavorites(businessId);
      if (success) {
        // Update the business in the list
        setNearbyBusinesses(prev => 
          prev.map(b => b.id === businessId ? { ...b, isFavorite: false } : b)
        );
        // Reload favorites to update the favorites card
        await loadFavorites();
      }
    } catch (error) {
      console.error('Error removing from favorites:', error);
    }
  };


  const handleQuickBook = () => {
    setActiveTab('business-exploration');
  };

  const handleTabPress = (tab: 'dashboard' | 'appointments' | 'work' | 'favorites' | 'business-exploration' | 'profile') => {
    if (tab === 'work' && memberships.length === 0) {
      Alert.alert('Work not available', 'No workspaces are linked to this account yet.');
      return;
    }
    setActiveTab(tab);
    // Home button should stay on dashboard, not navigate to chat
    // Only navigate to chat if explicitly requested
    // For appointments, favorites, business-exploration, and profile, the activeTab state will handle showing the correct screen
  };

  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const handleLogoutPress = () => {
    setShowSignOutModal(true);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatAddress = (address: any): string => {
    if (!address) return '';
    
    // If address is a string, return it as is
    if (typeof address === 'string') {
      return address;
    }
    
    // If address is an object, format it
    if (typeof address === 'object') {
      const parts = [
        address.street,
        address.city,
        address.state,
        address.zipcode,
        address.country
      ].filter(Boolean); // Remove empty values
      
      return parts.join(', ') || '';
    }
    
    return '';
  };

  const getUpcomingAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    return appointments.filter(apt => 
      apt.appointmentDate >= today && 
      (apt.status === 'confirmed' || apt.status === 'pending' || apt.status === 'pending_business_confirmation' || apt.status === 'pending_reschedule')
    ).slice(0, 3);
  };

  const getTodaysAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    return appointments.filter(apt =>
      apt.appointmentDate === today &&
      (apt.status === 'confirmed' || apt.status === 'pending' || apt.status === 'pending_business_confirmation' || apt.status === 'pending_reschedule')
    );
  };

  const getRecentAppointments = () => {
    return appointments.filter(apt => 
      apt.status === 'completed' || apt.status === 'paid'
    ).slice(0, 3);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Loading your dashboard...</Text>
      </View>
    );
  }

  // Show Appointments screen when appointments tab is selected
  if (activeTab === 'appointments') {
    return (
      <UserAppointmentsScreen
        onBack={() => setActiveTab('dashboard')}
        username={username}
        onExploreBusinesses={() => {
          // Navigate to business exploration screen
          setActiveTab('business-exploration');
        }}
        onTabPress={(tab) => {
          // When tab is pressed from favorites screen, ensure dashboard stays on dashboard
          if (tab === 'dashboard') {
            setActiveTab('dashboard');
          } else {
            handleTabPress(tab);
          }
        }}
        onLogout={onLogout}
        onNavigateToChat={onNavigateToChat}
        userFromParent={user}
        appointmentsFromParent={appointments}
        initialDateFilter={appointmentsInitialFilter as any}
      />
    );
  }

  // Show Work screen when work tab is selected
  if (activeTab === 'work') {
    return (
      <WorkAppointmentsScreen
        onBack={() => setActiveTab('dashboard')}
        onTabPress={(tab: 'dashboard' | 'appointments' | 'work' | 'favorites' | 'business-exploration' | 'profile') => {
          if (tab === 'dashboard') setActiveTab('dashboard');
          else handleTabPress(tab as any);
        }}
        onLogout={onLogout}
        memberships={memberships}
        activeMembership={activeMembership}
        onMembershipChange={setActiveMembership}
      />
    );
  }

  // Show Favorites screen when favorites tab is selected (but check for booking first)
  if (activeTab === 'favorites' && !showOneClickBooking) {
    console.log('🔍 Rendering UserFavoritesScreen with handleBookFromFavorites:', typeof handleBookFromFavorites);
    return (
      <UserFavoritesScreen
        onBookBusiness={handleBookFromFavorites}
        onExploreBusinesses={() => {
          // Navigate to business exploration screen
          setActiveTab('business-exploration');
        }}
        onTabPress={(tab) => {
          // When tab is pressed from favorites screen, ensure dashboard stays on dashboard
          if (tab === 'dashboard') {
            setActiveTab('dashboard');
          } else {
            handleTabPress(tab);
          }
        }}
        onLogout={onLogout}
      />
    );
  }

  // Show Business Exploration screen when business-exploration tab is selected (but check for booking first)
  if (activeTab === 'business-exploration' && !showOneClickBooking) {
    return (
      <BusinessExplorationScreen
        onBack={() => setActiveTab('favorites')}
        onBusinessSelect={(business) => {
          // Navigate to business details or chat
          if (onNavigateToChat) {
            onNavigateToChat();
          }
        }}
        onBookBusiness={handleBookFromFavorites}
        onTabPress={(tab) => {
          // When tab is pressed from favorites screen, ensure dashboard stays on dashboard
          if (tab === 'dashboard') {
            setActiveTab('dashboard');
          } else {
            handleTabPress(tab);
          }
        }}
        onLogout={onLogout}
      />
    );
  }

  // Show Profile screen when profile tab is selected
  if (activeTab === 'profile') {
    return (
      <UserProfileScreen
        onBack={() => setActiveTab('dashboard')}
        onLogout={onLogout}
        username={username}
        onTabPress={(tab) => {
          // When tab is pressed from favorites screen, ensure dashboard stays on dashboard
          if (tab === 'dashboard') {
            setActiveTab('dashboard');
          } else {
            handleTabPress(tab);
          }
        }}
        onNavigateToChat={onNavigateToChat}
        onNavigateToSubscription={onNavigateToSubscription}
      />
    );
  }

  // Show Credits Purchase screen
  if (showCreditsPurchase) {
    return (
      <CreditsPurchaseScreen
        onBack={() => setShowCreditsPurchase(false)}
        onPurchaseSuccess={() => {
          setShowCreditsPurchase(false);
          // Refresh dashboard data
          loadUserData();
        }}
      />
    );
  }

  // Show 30 Sec Booking screen when booking is active
  if (showOneClickBooking) {
    const widgetEmbedMode = isWidgetEmbedMode();
    return (
      <View style={styles.mainContainer}>
        <OneClickBookingScreen
          onBack={() => {
            if (widgetEmbedMode) {
              notifyWidgetEmbedParent('close');
            }
            setWidgetPreSelectedServiceId(null);
            setShowOneClickBooking(false);
          }}
          userId={user?.id || username || 'test_user'}
          user={user}
          onBookingComplete={handleBookingComplete}
          preSelectedBusiness={selectedBusiness}
          preSelectedServiceId={widgetPreSelectedServiceId || undefined}
          onNavigateToBusinessExploration={() => {
            if (widgetEmbedMode) {
              notifyWidgetEmbedParent('close');
            }
            setWidgetPreSelectedServiceId(null);
            setShowOneClickBooking(false);
            setActiveTab('business-exploration');
          }}
        />
        {!widgetEmbedMode ? (
          /* Bottom Menu - Keep visible on booking page */
          <UserBottomMenu
            activeTab={activeTab}
            onTabPress={(tab) => {
              if (tab === 'dashboard') {
                setWidgetPreSelectedServiceId(null);
                setShowOneClickBooking(false);
                setActiveTab('dashboard');
              } else {
                setWidgetPreSelectedServiceId(null);
                setShowOneClickBooking(false);
                handleTabPress(tab);
              }
            }}
            onLogout={onLogout}
          />
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.mainContainer}>
      <SignOutConfirmModal
        visible={showSignOutModal}
        onCancel={() => setShowSignOutModal(false)}
        onConfirm={() => {
          setShowSignOutModal(false);
          onLogout();
        }}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <Text style={styles.greeting}>{getGreeting()},</Text>
            <Text style={styles.userName}>{user?.email || user?.name || username || 'User'}!</Text>
            <Text style={styles.location}>
              📍 {formatAddress(user?.address) || 'Arlington, TX 76010'}
            </Text>
            {loadingCredits ? (
              <Text style={styles.creditsText}>Loading credits...</Text>
            ) : userCredits ? (
              <View style={styles.creditsContainer}>
                <Text style={styles.creditsText}>
                  {userCredits.availableCredits} Credits
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    if (onNavigateToCreditsPurchase) {
                      onNavigateToCreditsPurchase();
                    } else {
                      setShowCreditsPurchase(true);
                    }
                  }}
                >
                  <Text style={styles.buyCreditsLink}>Buy Credits</Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
          <View style={styles.headerButtons}>
            <TouchableOpacity style={styles.logoutButton} onPress={handleLogoutPress}>
              <Text style={styles.logoutButtonText}>Sign out</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Web Version Banner - Mobile Only */}
      {Platform.OS !== 'web' && (
        <View style={styles.webVersionBanner}>
          <View style={styles.webVersionBannerContent}>
            <Text style={styles.webVersionBannerIcon}>💻</Text>
            <View style={styles.webVersionBannerText}>
              <Text style={styles.webVersionBannerTitle}>Also available on web!</Text>
              <Text style={styles.webVersionBannerSubtitle}>Visit bookapt.io for desktop access</Text>
            </View>
            <TouchableOpacity
              style={styles.webVersionBannerButton}
              onPress={() => {
                Linking.openURL('https://bookapt.io/').catch(err => {
                  Alert.alert('Error', 'Could not open website. Please visit bookapt.io in your browser.');
                });
              }}
            >
              <Text style={styles.webVersionBannerButtonText}>Visit</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Explore card removed so dashboard loads fast; geocoding + Foursquare run when user taps Explore (bottom nav or "Explore Businesses" in Favorites). */}

      {/* Today's Appointments Block (replaces Your Favorites) */}
      <View style={styles.favoriteBusinessesBlockCompact}>
        <View style={styles.blockHeader}>
          <View style={styles.todaysBlockTitleRow}>
            <Image
              source={require('../assets/images/bookapt-logo.png')}
              style={styles.todaysBlockLogo}
              resizeMode="contain"
            />
            <Text style={styles.blockTitle}>Today&apos;s Appointments</Text>
          </View>
          <Text style={styles.blockSubtitle}>Appointments scheduled for today</Text>
        </View>
        {getTodaysAppointments().length > 0 ? (
          <View style={styles.favoriteBusinessesList}>
            {getTodaysAppointments().slice(0, 5).map((apt) => (
              <TouchableOpacity
                key={apt.id}
                style={styles.todaysAppointmentCard}
                onPress={() => {
                  setAppointmentsInitialFilter('today');
                  setActiveTab('appointments');
                }}
                activeOpacity={0.7}
              >
                <View style={styles.todaysAppointmentInfo}>
                  <Text style={styles.todaysAppointmentBusiness} numberOfLines={1}>
                    {apt.businessName || 'Business'}
                  </Text>
                  <Text style={styles.todaysAppointmentTime}>
                    {apt.appointmentTime} · {apt.serviceType}
                  </Text>
                </View>
                <Text style={styles.todaysAppointmentArrow}>›</Text>
              </TouchableOpacity>
            ))}
          </View>
        ) : (
          <View style={styles.noTodaysAppointmentsContainer}>
            <Image
              source={require('../assets/images/bookapt-logo.png')}
              style={styles.noTodaysAppointmentsIcon}
              resizeMode="contain"
            />
            <Text style={styles.noTodaysAppointmentsTitle}>No appointments today</Text>
            <Text style={styles.noTodaysAppointmentsText}>
              You don&apos;t have any appointments scheduled for today.
            </Text>
            <TouchableOpacity
              style={styles.viewAppointmentsButton}
              onPress={() => {
                setAppointmentsInitialFilter('today');
                setActiveTab('appointments');
              }}
            >
              <Text style={styles.viewAppointmentsButtonText}>View All Appointments</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* 30 Sec Booking Block - NOW IN MIDDLE (large size) */}
      <View style={styles.oneClickBookingBlockLarge}>
        <View style={styles.blockHeader}>
          <Text style={styles.blockTitle}>🚀 30 Sec Booking</Text>
          <Text style={styles.blockSubtitle}>Book appointments in 30 seconds</Text>
        </View>
        <TouchableOpacity
          style={styles.oneClickBookingButtonLarge}
          onPress={handleOneClickBooking}
        >
          <View style={styles.oneClickContentLarge}>
            <Text style={styles.oneClickIconLarge}>⚡</Text>
            <View style={styles.oneClickTextContainer}>
              <Text style={styles.oneClickTitleLarge}>Choose Services</Text>
              <Text style={styles.oneClickDescriptionLarge}>Select service, time & pay instantly</Text>
            </View>
            <Text style={styles.oneClickArrowLarge}>›</Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* Chat to Book Block - NEW CARD */}
      <View style={styles.chatToBookBlock}>
        <View style={styles.blockHeader}>
          <Text style={styles.blockTitle}>Chat to Book</Text>
          <Text style={styles.blockSubtitle}>Book appointments via chat</Text>
        </View>
        <TouchableOpacity
          style={styles.chatToBookButton}
          onPress={() => {
            if (onNavigateToChat) {
              onNavigateToChat();
            } else {
              setActiveTab('business-exploration');
            }
          }}
        >
          <View style={styles.chatToBookContent}>
            <View style={styles.chatToBookTextContainer}>
              <Text style={styles.chatToBookTitle}>
                <Text>💬 </Text>
                <Text>Start Chat</Text>
              </Text>
            </View>
            <Text style={styles.chatToBookArrow}>›</Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* Upcoming Appointments */}
      {getUpcomingAppointments().length > 0 && (
        <View style={styles.appointmentsContainer}>
          <Text style={styles.sectionTitle}>Upcoming Appointments</Text>
          {getUpcomingAppointments().map((appointment) => (
            <View key={appointment.id} style={styles.appointmentCard}>
              <View style={styles.appointmentHeader}>
                <Text style={styles.appointmentService}>{appointment.serviceType}</Text>
                <View style={[styles.statusBadge, { backgroundColor: '#007AFF' }]}>
                  <Text style={styles.statusText}>{appointment.status}</Text>
                </View>
              </View>
              <View style={styles.appointmentDateRow}>
                <Image source={require('../assets/images/bookapt-logo.png')} style={styles.bookaptIconSmall} resizeMode="contain" />
                <Text style={styles.appointmentDate}>{appointment.appointmentDate} at {appointment.appointmentTime}</Text>
              </View>
              <Text style={styles.appointmentLocation}>
                📍 {appointment.address}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* Recent Appointments */}
      {getRecentAppointments().length > 0 && (
        <View style={styles.appointmentsContainer}>
          <Text style={styles.sectionTitle}>Recent Appointments</Text>
          {getRecentAppointments().map((appointment) => (
            <View key={appointment.id} style={styles.appointmentCard}>
              <View style={styles.appointmentHeader}>
                <Text style={styles.appointmentService}>{appointment.serviceType}</Text>
                <View style={[styles.statusBadge, { backgroundColor: '#34C759' }]}>
                  <Text style={styles.statusText}>{appointment.status}</Text>
                </View>
              </View>
              <View style={styles.appointmentDateRow}>
                <Image source={require('../assets/images/bookapt-logo.png')} style={styles.bookaptIconSmall} resizeMode="contain" />
                <Text style={styles.appointmentDate}>{appointment.appointmentDate} at {appointment.appointmentTime}</Text>
              </View>
              <Text style={styles.appointmentLocation}>
                📍 {appointment.address}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* No Appointments Message */}
      {appointments.length === 0 && (
        <View style={styles.noAppointmentsContainer}>
          <Text style={styles.noAppointmentsTitle}>No Appointments Yet</Text>
          <Text style={styles.noAppointmentsText}>
            Start by booking an appointment with one of the nearby businesses above!
          </Text>
          <TouchableOpacity style={styles.bookFirstButton} onPress={handleQuickBook}>
            <Text style={styles.bookFirstButtonText}>Book Your First Appointment</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Powered by ORYN Inc. Footer */}
      <View style={styles.poweredByContainer}>
        <TouchableOpacity 
          onPress={() => Linking.openURL('https://orynsolutions.io')}
          activeOpacity={0.7}
        >
          <Text style={styles.poweredByText}>Powered by Oryn Inc.</Text>
        </TouchableOpacity>
      </View>

      </ScrollView>

      {/* Bottom Menu */}
      {!isWidgetEmbedMode() ? (
        <UserBottomMenu
          activeTab={activeTab}
          showWorkTab={memberships.length > 0}
          onTabPress={(tab) => {
            // When tab is pressed from favorites screen, ensure dashboard stays on dashboard
            if (tab === 'dashboard') {
              setActiveTab('dashboard');
            } else {
              handleTabPress(tab);
            }
          }}
          onLogout={onLogout}
        />
      ) : null}

      {/* Business Booking Modal */}
      <BusinessBookingModal
        visible={showBookingModal}
        business={selectedBusiness}
        onClose={() => setShowBookingModal(false)}
        onBookNow={handleBookingModalBook}
        onAddToFavorites={handleAddToFavorites}
        onRemoveFromFavorites={handleRemoveFromFavorites}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 70, // Space for bottom menu (consistent across all screens)
    flexGrow: 1,
    minHeight: '100%', // Ensure content takes full height even when empty
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    paddingTop: 50,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: 1,
  },
  greeting: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 2,
  },
  userName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 4,
  },
  location: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 6,
  },
  creditsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  creditsText: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    marginRight: 8,
  },
  buyCreditsLink: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 1)',
    textDecorationLine: 'underline',
    fontWeight: '600',
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoutButton: {
    backgroundColor: 'rgba(255,255,255,0.3)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.5)',
  },
  logoutButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textShadowColor: 'rgba(0,0,0,0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  nearbyBusinessesContainer: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  businessCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  businessHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  businessInfo: {
    flex: 1,
  },
  businessName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  businessCategory: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  businessRating: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ratingText: {
    fontSize: 14,
    color: '#FF9500',
    marginRight: 12,
  },
  distanceText: {
    fontSize: 14,
    color: '#666',
  },
  businessStatus: {
    alignItems: 'flex-end',
  },
  statusIndicator: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 4,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  nextSlotText: {
    fontSize: 12,
    color: '#007AFF',
    fontWeight: '500',
  },
  businessAddress: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  businessPhone: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  servicesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  serviceTag: {
    backgroundColor: '#F0F8FF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  serviceText: {
    fontSize: 12,
    color: '#007AFF',
    fontWeight: '500',
  },
  businessActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 15,
    gap: 10,
  },
  chatButton: {
    flex: 1,
    backgroundColor: '#F3F4F6', // Non-highlighted: white/gray background
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB', // Gray border
    alignItems: 'center',
  },
  chatButtonText: {
    color: '#6B7280', // Gray text for non-highlighted
    fontSize: 14,
    fontWeight: '600',
  },
  bookNowButton: {
    flex: 1,
    backgroundColor: '#2563EB', // Professional blue - highlighted
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  bookNowButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  appointmentsContainer: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  appointmentCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  appointmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  appointmentService: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  appointmentDateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  bookaptIconSmall: {
    width: 18,
    height: 18,
    marginRight: 8,
  },
  appointmentDate: {
    fontSize: 14,
    color: '#666',
  },
  appointmentLocation: {
    fontSize: 14,
    color: '#666',
  },
  noAppointmentsContainer: {
    alignItems: 'center',
    padding: 40,
    marginHorizontal: 20,
  },
  noAppointmentsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 12,
  },
  noAppointmentsText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  bookFirstButton: {
    backgroundColor: '#2563EB', // Professional blue
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  bookFirstButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  subscriptionStatusContainer: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  subscriptionCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  subscriptionPlan: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  subscriptionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  upgradeButton: {
    backgroundColor: '#2563EB', // Professional blue
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  upgradeButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  trialCard: {
    backgroundColor: '#E8F5E8',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#34C759',
  },
  trialPlan: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2E7D32',
    marginBottom: 4,
  },
  trialDescription: {
    fontSize: 14,
    color: '#2E7D32',
    marginBottom: 4,
  },
  trialNote: {
    fontSize: 12,
    color: '#4CAF50',
    marginBottom: 12,
    fontStyle: 'italic',
  },
  manageTrialButton: {
    backgroundColor: '#7C3AED', // Single primary color
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  manageTrialButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  expiredTrialCard: {
    backgroundColor: '#FFEBEE',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#FFCDD2',
  },
  expiredTrialPlan: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#C62828',
    marginBottom: 4,
  },
  expiredTrialDescription: {
    fontSize: 14,
    color: '#D32F2F',
    marginBottom: 12,
  },
  // One-Click Booking Styles
  oneClickBookingContainer: {
    backgroundColor: 'white',
    padding: 20,
    marginBottom: 10,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  oneClickButton: {
    backgroundColor: '#2563EB', // Professional blue
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 15,
    shadowColor: '#2563EB',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  oneClickButtonIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  oneClickButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  oneClickButtonSubtext: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 14,
  },
  instantBookingsContainer: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
  },
  instantBookingsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 15,
  },
  instantBookingCard: {
    backgroundColor: '#F8F9FA',
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  instantBookingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  instantBookingService: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  instantBookingDate: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 4,
  },
  instantBookingPrice: {
    fontSize: 14,
    color: '#34C759',
    fontWeight: '600',
  },
  // New Professional Two-Block Layout Styles
  // Business Exploration Block - Compact (at top)
  businessExplorationBlockCompact: {
    backgroundColor: 'white',
    marginHorizontal: 20,
    marginTop: 20,
    marginBottom: 16,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    minHeight: 140,
  },
  businessExplorationButton: {
    padding: 20,
    backgroundColor: '#F8F9FF',
  },
  businessExplorationContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  businessExplorationIcon: {
    fontSize: 32,
    marginRight: 16,
  },
  businessExplorationTextContainer: {
    flex: 1,
  },
  businessExplorationTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#007AFF',
    marginBottom: 4,
  },
  businessExplorationDescription: {
    fontSize: 14,
    color: '#8E8E93',
  },
  businessExplorationArrow: {
    fontSize: 24,
    color: '#007AFF',
    fontWeight: 'bold',
  },
  // Favorites Block - Compact (below exploration)
  favoriteBusinessesBlockCompact: {
    backgroundColor: 'white',
    marginHorizontal: 20,
    marginTop: 0,
    marginBottom: 16,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    minHeight: 140,
  },
  // 30 Sec Booking Block - Large (bigger size in middle)
  oneClickBookingBlockLarge: {
    backgroundColor: 'white',
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 6,
    minHeight: 280,
  },
  // Legacy styles (keeping for compatibility)
  oneClickBookingBlock: {
    backgroundColor: 'white',
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  favoriteBusinessesBlock: {
    backgroundColor: 'white',
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  blockHeader: {
    padding: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  todaysBlockTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  todaysBlockLogo: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  blockTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  blockSubtitle: {
    fontSize: 14,
    color: '#8E8E93',
  },
  // Large 30 Sec Booking Button Styles
  chatToBookBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginHorizontal: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  chatToBookButton: {
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    padding: 16,
    marginTop: 12,
  },
  chatToBookContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  chatToBookIcon: {
    fontSize: 32,
    marginRight: 16,
  },
  chatToBookTextContainer: {
    flex: 1,
  },
  chatToBookTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  chatToBookDescription: {
    fontSize: 14,
    color: '#8E8E93',
  },
  chatToBookArrow: {
    fontSize: 24,
    color: '#8E8E93',
    fontWeight: '300',
  },
  oneClickBookingButtonLarge: {
    padding: 30,
    backgroundColor: '#F8F9FF',
  },
  oneClickContentLarge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  oneClickIconLarge: {
    fontSize: 64,
    marginRight: 20,
  },
  oneClickTitleLarge: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#007AFF',
    marginBottom: 8,
  },
  oneClickDescriptionLarge: {
    fontSize: 14,
    color: '#1C1C1E',
    lineHeight: 20,
  },
  oneClickArrowLarge: {
    fontSize: 32,
    color: '#007AFF',
    fontWeight: 'bold',
  },
  // Original compact styles (for legacy support)
  oneClickBookingButton: {
    padding: 20,
  },
  oneClickContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  oneClickIcon: {
    fontSize: 32,
    marginRight: 16,
  },
  oneClickTextContainer: {
    flex: 1,
  },
  oneClickTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  oneClickDescription: {
    fontSize: 14,
    color: '#8E8E93',
  },
  oneClickArrow: {
    fontSize: 24,
    color: '#C7C7CC',
    fontWeight: 'bold',
  },
  favoriteBusinessesList: {
    padding: 20,
    paddingTop: 0,
  },
  favoriteBusinessCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  favoriteBusinessInfo: {
    flex: 1,
  },
  favoriteBusinessName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  favoriteBusinessCategory: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 4,
  },
  favoriteBusinessRating: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  favoriteRatingText: {
    fontSize: 12,
    color: '#FFC107',
    fontWeight: '600',
    marginRight: 8,
  },
  favoriteDistanceText: {
    fontSize: 12,
    color: '#8E8E93',
  },
  favoriteBusinessActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  favoriteChatButton: {
    backgroundColor: '#F3F4F6', // Non-highlighted: white/gray background
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB', // Gray border
  },
  favoriteChatButtonText: {
    fontSize: 16,
    color: '#6B7280', // Gray text for non-highlighted
  },
  favoriteBookButton: {
    backgroundColor: '#2563EB', // Professional blue - highlighted
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  favoriteBookButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  todaysAppointmentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  todaysAppointmentInfo: {
    flex: 1,
  },
  todaysAppointmentBusiness: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  todaysAppointmentTime: {
    fontSize: 14,
    color: '#8E8E93',
  },
  todaysAppointmentArrow: {
    fontSize: 24,
    color: '#007AFF',
    fontWeight: 'bold',
  },
  noTodaysAppointmentsContainer: {
    alignItems: 'center',
    padding: 40,
  },
  noTodaysAppointmentsIcon: {
    width: 56,
    height: 56,
    marginBottom: 16,
    opacity: 0.85,
  },
  noTodaysAppointmentsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  noTodaysAppointmentsText: {
    fontSize: 14,
    color: '#8E8E93',
    textAlign: 'center',
    marginBottom: 20,
  },
  viewAppointmentsButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
  },
  viewAppointmentsButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  noFavoritesContainer: {
    alignItems: 'center',
    padding: 40,
  },
  noFavoritesIcon: {
    fontSize: 48,
    color: '#C7C7CC',
    marginBottom: 16,
  },
  noFavoritesTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  noFavoritesText: {
    fontSize: 14,
    color: '#8E8E93',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  exploreFavoritesButton: {
    backgroundColor: '#2563EB', // Professional blue
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  exploreFavoritesButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  poweredByContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    paddingBottom: 30,
    marginTop: 20,
  },
  poweredByText: {
    fontSize: 12,
    color: '#999',
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  // Web Version Banner Styles
  webVersionBanner: {
    backgroundColor: '#E6F2FF',
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
    marginHorizontal: 20,
    marginTop: 10,
    marginBottom: 10,
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  webVersionBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  webVersionBannerIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  webVersionBannerText: {
    flex: 1,
    marginRight: 12,
  },
  webVersionBannerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  webVersionBannerSubtitle: {
    fontSize: 12,
    color: '#666',
  },
  webVersionBannerButton: {
    backgroundColor: '#2563EB', // Professional blue
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  webVersionBannerButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default UserDashboardScreen;
